import * as React from 'react';
import cs from 'classnames';
import { connect, ConnectedProps } from 'react-redux';
import { some } from 'lodash';
import { SearchListing, SearchCriteria, StoreState } from '@root/types';
import savedActions from '@store/actions/saved';
import actions from '@store/actions/tourbooks';
import {
  hasCurrentUser,
  isListingSaved,
  getVisibleTourbookSummaries,
  tourbookCreateOnly,
} from '@store/selectors';
import Pagination from '@components/shared/Pagination';
import { useLocation, useParams } from 'react-router-dom';
import { ListingSearchCriteria } from '@components/layouts/Truva/ListingSearchDeprecated/utils';
import ListingCard from './ListingCard';
import ResultsEmptyState from './ResultsEmptyState';
import s from './Cards.module.less';

export type PassedProps = {
  currentPage: number;
  searchCardClicked: (
    e: React.MouseEvent,
    listing: SearchListing,
    searchFilters: SearchCriteria,
  ) => void;
  searchCardHovered: (listing: SearchListing | null) => void;
  changePage: (newPage: number) => void;
};

const mapState = (state: StoreState) => ({
  listingsCount: state.listings.pagination?.totalCount,
  ids: state.listings.pagination?.ids,
  pageSize: state.listings.pagination?.pageSize,
  listingsById: state.listings.searchResults,
  isSaved: (listingId: string) => isListingSaved(state, listingId),
  someoneIsLoggedIn: hasCurrentUser(state),
  tourbooks: getVisibleTourbookSummaries(state),
  istourbookCreateOnly: tourbookCreateOnly(state),
});

const mapDispatch = {
  toggleSaveListing: ({ listing, willBeSaved, onSuccess }) =>
    savedActions.toggleSaveListing({
      listing,
      willBeSaved,
      onSuccess,
      sourcePage: 'search results page',
    }),
  fetchTourbooks: () => actions.fetchTourbookSummaries(),
};

const connector = connect(mapState, mapDispatch);
export type ReduxProps = ConnectedProps<typeof connector>;

export const RawCards = ({
  listingsCount = 0,
  ids = [],
  listingsById,
  searchCardClicked,
  searchCardHovered,
  changePage,
  pageSize = 24,
  currentPage,
  toggleSaveListing,
  isSaved,
  someoneIsLoggedIn,
  tourbooks,
  istourbookCreateOnly,
  fetchTourbooks,
}: PassedProps & ReduxProps) => {
  const hasSearchResults = ids && ids.length;
  const idsIterator = ids || [];

  const { search: queryString } = useLocation();
  const { marketSlug } = useParams<{ marketSlug: string }>();

  React.useEffect(() => {
    if (someoneIsLoggedIn) fetchTourbooks();
  }, [someoneIsLoggedIn]);

  const criteria = ListingSearchCriteria.fromUrl({ marketSlug, queryString });

  const isInTourbook = (listingId: string): boolean =>
    some(tourbooks, tourbook => tourbook.listingIds.includes(listingId));

  return (
    <section className={s.container}>
      <ul className={s.cardList} id="listing-cards">
        {hasSearchResults ? (
          idsIterator.map((listingId, i) => {
            const listing = listingsById[listingId];
            if (!listing) return null;

            return (
              <li
                className={cs(s.listingCardWrapper, (i + 1) % 3 === 0 ? s.lastCard : null)}
                key={listingId}
              >
                <ListingCard
                  listing={listing}
                  onClick={e => searchCardClicked(e, listing, criteria.filters())}
                  onHover={() => searchCardHovered(listing)}
                  onHoverExit={() => searchCardHovered(null)}
                  toggleSaveListing={toggleSaveListing}
                  favorited={isSaved(listingId)}
                  loggedInUser={someoneIsLoggedIn}
                  tourbooks={tourbooks}
                  isInTourbook={isInTourbook(listingId)}
                  istourbookCreateOnly={istourbookCreateOnly}
                />
              </li>
            );
          })
        ) : (
          <ResultsEmptyState />
        )}
      </ul>
      <div className={s.paginationContainer}>
        <Pagination
          currentPage={currentPage}
          total={listingsCount}
          pageSize={pageSize}
          testId="pagination"
          onChange={changePage}
        />
      </div>
    </section>
  );
};

export default connector(RawCards);
